import { EndpointsName, get } from './../../Auth';
import { forkJoin, Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import {
    AlarmStatus,
    Area,
    IMLCWarningIndexStatusResponse,
    MLCINTBWarningIndexGroupedData,
    MLCOverviewData,
    MLCTBCFWarningIndexGroupedData,
    MLCWarningIndexGroupedData,
    MLCWarningIndexStatusData,
    ProcessLine,
    WarningIndexSingleParamData,
    WarningIndexStatuses
} from "../../types";

const buildGetMLCWarningIndexEndpoint = (area: Area, line?: ProcessLine) => `/mlc/${area}/${line ? `${line}/` : ''}warningindex`

const getMLCINTBWIData = (line?: ProcessLine) => get<MLCWarningIndexGroupedData>(EndpointsName.BBF, buildGetMLCWarningIndexEndpoint(Area.INTB, line));

const getMLCTBCFWIData = () => get<MLCTBCFWarningIndexGroupedData>(EndpointsName.BBF, buildGetMLCWarningIndexEndpoint(Area.TBCF));

const parseWIToIntIfExist = (data: WarningIndexSingleParamData) => {
    if (data.Warning_Index !== undefined) {
        data.Warning_Index = parseInt(`${data.Warning_Index}`)
    }
};

const setBasicINTBInfo = (data: MLCWarningIndexGroupedData, line: ProcessLine) => {
    if (data && Object.keys(data).length) {
        for (const key in data) {
            parseWIToIntIfExist(data[key]);
            data[key].ParamName = key.replace(/_/g, ' ');
            data[key].Line = line
        }
    }
    return data
};
// TO TEST DCCC2
const testDCCC2Param: any = { 'TB_BU-BR_Delta_OV_Deviation_TotalBU_OV': { ParamName: 'TB BU-BR Delta OV Deviation TotalBU OV', getTarget: true, Warning_Index: 100, Line: 'DCCC2', ParameterGroup: "Delta OV" } };
export const getMLCINTBWarningIndexInitialData = (): Observable<MLCINTBWarningIndexGroupedData> => forkJoin(
    {
        DCCC1: getMLCINTBWIData(ProcessLine.DCCC1).pipe(
            map(data => setBasicINTBInfo(data, ProcessLine.DCCC1)),
        ),
        DCCC2: getMLCINTBWIData(ProcessLine.DCCC2).pipe(
            // mapTo(testDCCC2Param), // TO TEST DCCC2
            map(data => setBasicINTBInfo(data, ProcessLine.DCCC2)),
        ),
    }
).pipe(
    tap(data => (global as any).PMI_LOGS && console.debug('INITIAL/MLC/INTB/WI: ', data)),
);

export const getMLCTBCFWarningIndexInitialData = (): Observable<MLCTBCFWarningIndexGroupedData> => getMLCTBCFWIData().pipe(
    map(data => {
        for (const key in data) {
            parseWIToIntIfExist(data[key]);
            data[key].ParamName = key.replace(/_/g, ' ');
        }
        return data
    }),
).pipe(
    tap(data => (global as any).PMI_LOGS && console.debug('INITIAL/MLC/TBCF/WI: ', data)),
);


// OVERVIEW
const buildGetMLCOverviewEndpoint = (area: Area, line?: ProcessLine) => `/mlc/${area}/${line ? `${line}/` : ''}overview`;

export const getMLCINTBOverviewData = (line: ProcessLine) => get<MLCOverviewData>(EndpointsName.BBF, buildGetMLCOverviewEndpoint(Area.INTB, line));
export const getMLCTBCFOverviewData = () => get<MLCOverviewData>(EndpointsName.BBF, buildGetMLCOverviewEndpoint(Area.TBCF));


// WARNINGSTATUS
const ackAction = 'Acknowledge'
const mapWarningStatusByPriority = (data: IMLCWarningIndexStatusResponse[]): [WarningIndexStatuses, AlarmStatus] => {

    const values = data.reduce((acc, area) => {
        const areaValues = Object.values(area).filter(Boolean)
        return acc.concat(areaValues)
    }, [] as WarningIndexStatuses[])

    if (values.find(status => status === WarningIndexStatuses.ERROR)) {
        const isAck = data.some(area => Object.entries(area)
            .filter(([key, _value]) => {
                const [_param, action] = key.split(' ');
                return action === ackAction
            }).map(([_key, value]) => value)
            .some(Boolean));
        return [WarningIndexStatuses.ERROR, isAck ? AlarmStatus.NO : AlarmStatus.YES]
    }

    if (values.find(status => status === WarningIndexStatuses.WARNING)) {
        return [WarningIndexStatuses.WARNING, AlarmStatus.NO]
    }

    return [WarningIndexStatuses.OK, AlarmStatus.NO]
}
const getINTBWarningStatus = (): Observable<[WarningIndexStatuses, AlarmStatus]> => get<IMLCWarningIndexStatusResponse[]>(EndpointsName.BBF, '/mlc/intb/warningindex/status').pipe(
    map(mapWarningStatusByPriority)
)
const getTBCFWarningStatus = (): Observable<[WarningIndexStatuses, AlarmStatus]> => get<IMLCWarningIndexStatusResponse[]>(EndpointsName.BBF, '/mlc/tbcf/warningindex/status').pipe(
    map(mapWarningStatusByPriority)
)


export const getMLCWarningStatus = (): Observable<MLCWarningIndexStatusData> => forkJoin({
    intb: getINTBWarningStatus(),
    tbcf: getTBCFWarningStatus()
}).pipe(
    map(({
        intb,
        tbcf
    }) => ({
        InTbStatus: intb[0],
        TbCfStatus: tbcf[0],
        TurnAlarm: tbcf[1] || intb[1]
    }))
)